import type { Rol } from "@copi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { authApi } from "./api/auth.api";

import { ApiError } from "@/lib/api/api-error";
import { conectarRefreshHandler } from "@/lib/api/client";
import { tokenStore } from "@/lib/api/token-store";

export interface AuthUser {
  id: string;
  correo: string;
  nombres: string;
  apellidos: string;
  rol: Rol;
  /** Capacidades del docente (ADR 054). `false` para quien no es docente. */
  puedeAsesorar: boolean;
  puedeSerJurado: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (correo: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Provider de autenticación.
 *
 * Estrategia (convenciones §8.2):
 *  - Access token en memoria (tokenStore) — anti-XSS.
 *  - Refresh token en cookie httpOnly — el backend lo lee solo.
 *  - Al montar, intenta `/auth/refresh`: si la cookie es válida, recupera sesión.
 *  - Conecta el handler de refresh en el cliente axios para auto-renovar on 401.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const aplicarUsuario = useCallback(async (token: string) => {
    tokenStore.set(token);
    const me = await authApi.me();
    setUser({
      id: me.id,
      correo: me.correo,
      nombres: me.nombres,
      apellidos: me.apellidos,
      rol: me.rol as Rol,
      puedeAsesorar: me.puedeAsesorar,
      puedeSerJurado: me.puedeSerJurado,
    });
  }, []);

  const login = useCallback(
    async (correo: string, password: string) => {
      // Purga cualquier dato cacheado de una sesión anterior ANTES de aplicar
      // el nuevo usuario. Las queries del dominio (panel del estudiante,
      // bandejas, expedientes) se cachean con keys que NO incluyen el userId
      // — son "los míos" desde la perspectiva del token. Sin este clear, al
      // cambiar de usuario en la misma pestaña (logout → login) se mostraban
      // los datos del usuario anterior hasta el primer refetch.
      queryClient.clear();
      const { accessToken } = await authApi.login({ correo, password });
      await aplicarUsuario(accessToken);
    },
    [aplicarUsuario, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Si el logout HTTP falla (token ya inválido, etc.) limpiamos local igual.
    }
    tokenStore.set(null);
    setUser(null);
    // Purga el cache de TanStack Query: los datos del usuario que sale no
    // deben sobrevivir para el próximo login en la misma pestaña.
    queryClient.clear();
  }, [queryClient]);

  // Boot: intentar restaurar sesión vía refresh.
  useEffect(() => {
    let cancelado = false;

    async function restaurar() {
      try {
        const { accessToken } = await authApi.refresh();
        if (cancelado) return;
        await aplicarUsuario(accessToken);
      } catch (err) {
        // 401/network → no hay sesión, queda como anónimo.
        if (
          !ApiError.esApiError(err) &&
          err instanceof Error &&
          err.message !== "REFRESH_TOKEN_MISSING"
        ) {
          console.warn("auth restore failed:", err);
        }
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    }

    void restaurar();
    return () => {
      cancelado = true;
    };
  }, [aplicarUsuario]);

  // Conectar handler de refresh on 401 al cliente axios.
  useEffect(() => {
    conectarRefreshHandler(async () => {
      try {
        const { accessToken } = await authApi.refresh();
        tokenStore.set(accessToken);
        return accessToken;
      } catch {
        tokenStore.set(null);
        setUser(null);
        return null;
      }
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, isLoading, login, logout }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  }
  return ctx;
}
